/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FC, ReactNode, memo, useCallback } from 'react';
import { QSButton, QSTypography } from '../../../../../shared/components/atoms';
import { openPopup } from '../../../../../shared/utils/popup';
import EmailIcon from '../../../../../shared/icons/icons-revamp/email';
import WhatsappIcon from '../../../../../shared/icons/icons-revamp/whatsapp';
import { AgentAvatar } from '../../../../home/v1/atoms/agent-avatar';
import { OrgOwner } from './typings';

interface OwnerRowProps {
  owner: OrgOwner;
  featureName?: string;
}

interface ContactDetails {
  href: string;
  icon: ReactNode;
  subtitle: string;
}

const buildAccessMessage = (featureName?: string): string =>
  featureName ? `Requesting access to ${featureName}` : 'Requesting access';

const buildMailtoHref = (email: string, featureName?: string): string =>
  `mailto:${email}?subject=${encodeURIComponent(buildAccessMessage(featureName))}`;

const buildWhatsAppHref = (phone: string, featureName?: string): string => {
  const digits = phone.replace(/\D/g, '');
  return `https://wa.me/${digits}?text=${encodeURIComponent(buildAccessMessage(featureName))}`;
};

const getContactDetails = (owner: OrgOwner, featureName?: string): ContactDetails | undefined => {
  if (owner.email) {
    return {
      href: buildMailtoHref(owner.email, featureName),
      icon: <EmailIcon size={14} color="#7a7a7a" />,
      subtitle: owner.email,
    };
  }
  if (owner.phone) {
    return {
      href: buildWhatsAppHref(owner.phone, featureName),
      icon: <WhatsappIcon size={12} color="#7a7a7a" />,
      subtitle: owner.phone,
    };
  }
  return undefined;
};

const OwnerRow: FC<OwnerRowProps> = ({ owner, featureName }) => {
  const contact = getContactDetails(owner, featureName);

  const handleCtaClick = useCallback(() => {
    if (contact) {
      openPopup(contact.href, '_blank');
    }
  }, [contact]);

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px;
        height: 56px;
        box-sizing: border-box;
      `}
    >
      <AgentAvatar name={owner.name} phone={owner.phone} size={36} />
      <div
        css={css`
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          row-gap: 1px;
        `}
      >
        <QSTypography
          css={css`
            font-size: 14px;
            font-weight: 500;
            line-height: 16.875px;
            letter-spacing: -0.162px;
            color: #2d2d2d;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          `}
        >
          {owner.name}
        </QSTypography>
        {contact && (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 6px;
              min-width: 0;
            `}
          >
            <span
              css={css`
                display: inline-flex;
                flex-shrink: 0;
                line-height: 0;
              `}
            >
              {contact.icon}
            </span>
            <QSTypography
              css={css`
                font-size: 12px;
                font-weight: 400;
                line-height: 15.6px;
                letter-spacing: -0.036px;
                color: #7a7a7a;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              `}
            >
              {contact.subtitle}
            </QSTypography>
          </div>
        )}
      </div>
      {contact && (
        <QSButton
          variant="text"
          onClick={handleCtaClick}
          css={css`
            color: #307e5b;
            font-size: 12px;
            font-weight: 500;
            padding: 6px 10px;
            border-radius: 7px;
            min-width: auto;
            text-transform: none;
            &:hover {
              background-color: rgba(48, 126, 91, 0.08);
            }
          `}
        >
          Contact
        </QSButton>
      )}
    </div>
  );
};

export default memo(OwnerRow);
